import { FetchError } from 'ofetch';
import type { NitroFetchRequest, AvailableRouterMethod, NitroFetchOptions } from 'nitropack';
import type { AsyncData, FetchResult, NuxtError, UseFetchOptions } from 'nuxt/app';
import type { KeysOf, _AsyncData, PickFrom } from 'nuxt/dist/app/composables/asyncData';
import type { Ref, UnwrapRef } from 'vue';
import { getCurrentScope, onScopeDispose } from 'vue';
import { useDYUserStore } from '../stores/user';
import { DY_DISCARDED_REQUEST } from '../libs/discarded-request';
import { useDYAppConfig } from './config';
import { isJsonString, getFullDomain, hasOwnProperty, uniqueId, findIpForSSR, logToFile } from '../libs/utils';
import { convertErrorCode } from './common';
import md5 from 'crypto-js/md5'

type QueryKeys = 'params' | 'query'

/**
 * 2026-07-02 엣지(CF) 캐시 요청에 붙이는 KST 날짜 스탬프(YYYYMMDD). 자정마다 URL(=CF 캐시키)이 하루 한 번 바뀌어,
 * 날짜파생 응답필드(NEW 배지·D-N 카운트다운·isRecentUpdate 등)의 **날짜경계 stale 를 제거**한다(엣지 24h 여도 안전).
 *  - `Date.now()` 는 UTC epoch(서버/클라 어디서나 동일) → 고정 +9h(KST) 오프셋 → SSR/CSR·클라 TZ 무관하게 동일 값(하이드레이션 안전, 클라 로컬시각 쓰면 TZ 파편화+불일치).
 *  - L0 키(getOptionsKey)는 makebaseURLOptions/_dyBuildFetchOptions **이전에** 계산되므로 _edgeDay 를 포함하지 않는다 → 하이드레이션 키는 안정, _edgeDay 는 URL(엣지)만 회전.
 *  - **엣지 대상 판정 = query 에 `edgeCache` 가 있을 때만**(무토큰/board 게이팅 통과분 등 실제 엣지 요청). refresh(엣지 바이패스) 동반이어도 무해.
 * @see api-composable-cache 스킬(엣지 캐시 + 날짜 스탬프 방침)
 */
export function edgeDayStamp(): string {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10).replace(/-/g, '')
}

/**
 * 2026-07-20 관리자 요청 판정(scope group 'admin') — **composable 이 엣지 정책을 정할 때 쓰는 공유 헬퍼**.
 *
 * 용례: 관리자 여부로 응답이 갈리는 엔드포인트의 composable 이 `isDYEdgeAdmin() ? { edgeNoStore: 'y' } : { edgeCache: ... }`
 *   식으로 분기한다(fetch 레이어는 admin 을 판정하지 않는다 — 위 applyEdgeQueryPolicy 주석 참조).
 *
 * `useDYAuth().hasAuth('admin')`(useRoute 의존) 대신 user store 의 scope 를 직접 본다 — API composable 은
 * setup 밖(onMounted/watch/store action)에서도 호출되므로 "Component is already mounted" 경고를 피해야 한다.
 * group 매칭 규칙은 auth.ts `hasAuth` 와 동일(`'admin'` 또는 `'admin:xxx'`). dy-board.ts 의 국소 구현을 여기로 승격.
 */
export function isDYEdgeAdmin(): boolean {
  const userStore = useDYUserStore()
  return !!userStore.scope?.some((entry) => (entry.includes(':') ? entry.split(':')[0] : entry) === 'admin')
}

/**
 * 2026-07-20 [엣지 캐시 쿼리 정책 — 중앙 1곳] 나가는 요청의 엣지 관련 쿼리를 최종 확정한다.
 *
 * ## ① edgeNoStore 안전망 (신규)
 * `edgeNoStore=y`(= "이 요청은 엣지에 담기면 안 됨" 선언)가 실려 있으면 `edgeCache`/`_edgeDay` 를 **강제 제거**한다.
 * CF Cache Rule 은 `edgeCache` 파라미터 유무로만 캐시를 정하므로, 둘이 함께 실리면 선언이 무효가 되기 때문이다.
 *
 * ⚠️ **여기서 관리자 여부를 판정하지 않는다**(의도적). "이 엔드포인트가 관리자냐 아니냐로 응답이 갈리는가" 는
 *   fetch 레이어가 알 수 없다 — 관리자가 부르는 **공용 API**(도시/국가/콘텐츠 등)는 엣지를 그대로 타야 하고,
 *   앞으로 **관리자용 엣지 API** 도 점진 적용될 예정이라 일괄 admin 차단은 틀린 기본값이다.
 *   → 판정은 **엔드포인트를 아는 composable** 이 하고(`isDYEdgeAdmin()` 등), 결과만 `edgeNoStore` 로 넘긴다.
 *   선례: dy-board.ts `list`/`get`. @see EDGE-ADMIN-GATE.md
 *
 * ## ② _edgeDay 날짜 스탬프 (2026-07-02, 기존)
 * 엣지 대상으로 확정된 요청에만 KST 날짜 스탬프를 붙여 자정마다 CF 캐시키를 회전시킨다(날짜파생 필드 stale 제거).
 */
function applyEdgeQueryPolicy(newQuery: Record<string, any>): void {
  // 엣지 비대상 선언이 있으면 edgeCache 는 무조건 제거(둘 다 실리면 CF 가 캐시해버려 선언이 무효)
  if (newQuery.edgeNoStore && newQuery.edgeCache) delete newQuery.edgeCache

  if (newQuery.edgeCache) newQuery['_edgeDay'] = edgeDayStamp()
}

/*
 * 2026-07-27 [SSR 실패 방침] 재시도를 **의도적으로 추가하지 않는다.**
 *
 * 한때 SSR GET 에 재시도 기본값(retry 2회 / 300ms→800ms, 이후 1회 250ms)을 넣었다가 되돌렸다. 이유:
 *  - SSR 실패 경로가 느려지면 구글이 크롤 속도 자체를 낮춘다. 재시도로 버는 것보다 잃는 게 크다.
 *  - API 가 흔들릴 때 모든 페이지의 모든 하위 요청이 재시도하면 부하를 증폭시켜 장애를 키운다.
 *  - 무엇보다 **실패를 실패로 내보내는 편이 구글에게 올바른 신호**다. 5xx 를 받은 구글봇은
 *    크롤 속도를 낮추고 나중에 재시도하며 그동안 색인 상태를 유지한다.
 *
 * 따라서 재시도는 ofetch 기본값(GET 1회 / 딜레이 0ms)에 맡기고, 우리가 책임질 부분은
 * **실패를 정확한 상태코드로 전파하는 것**과 **페이지가 반쪽 데이터로 200 을 내보내지 않는 것**이다.
 * @see SSR-FAILURE-POLICY.md
 */

/** SSR GET 요청의 상한 대기시간(ms). @see applySsrTimeoutPolicy */
const SSR_GET_TIMEOUT_MS = 60_000

/**
 * 2026-07-27 [SSR 타임아웃] SSR 의 GET 요청에 60초 상한을 건다.
 *
 * 왜 필요한가 — 지금까지 타임아웃이 **어디에도 설정돼 있지 않았다.** Node 내장 fetch(undici) 기본값은
 * `headersTimeout`/`bodyTimeout` 이 각각 **300초(5분)** 라(undici/lib/client.js:284-285),
 * 상류가 "커넥션은 받아주는데 응답을 못 만드는" 상태(Apache 워커 고갈 / PHP 가 DB 락·외부 API 대기 등)에 빠지면
 * SSR 요청 하나가 **5분간 Node 이벤트루프 슬롯과 메모리를 붙잡는다.**
 * 그런 요청이 쌓이면 사이트 전체가 응답 불가가 된다. (실측: 응답 없는 서버에 15초가 지나도 안 끊김)
 *
 * 60초 근거 — 프로덕션 SSR 렌더 실측(그 페이지의 **모든** API 호출 합계)이 0.8~2.0초다.
 * 개별 호출은 그보다 훨씬 짧다. 60초는 그 30배로, 정상 요청이 걸릴 여지가 없다.
 * 해외 접속(지연·패킷 손실)까지 감안한 값이며, **속도를 강제하는 장치가 아니라 워커 고갈을 막는 안전장치**다.
 * 5분 → 60초로 최악 점유시간이 1/5 로 준다.
 *
 * 적용 범위
 * - **SSR 에서만.** 클라이언트에는 걸지 않는다(어드민은 ssr:false 라 애초에 무관).
 * - **GET 에서만.** POST/PUT/PATCH/DELETE 는 중단해도 서버는 처리를 끝냈을 수 있어,
 *   결제 같은 요청에서 "사용자는 실패로 봤는데 실제로는 결제됨" 이 될 수 있다. 손대지 않는다.
 * - 호출자가 `timeout` 을 명시했으면 그 값을 존중한다. 느린 엔드포인트는 개별 지정으로 늘리면 된다.
 *
 * 재시도와의 관계 — 타임아웃으로 중단된 요청을 ofetch 가 재시도하지는 않는다(실측: 1회 시도로 종료).
 * 따라서 `retry` 를 건드리지 않아도 60초가 곧 실질 상한이다.
 * 중단된 요청은 `_resolveUpstreamStatusCode` 가 **504** 로 분류한다.
 */
function applySsrTimeoutPolicy(opts: Record<string, any>): void {
  if (!import.meta.server) return
  if (opts.timeout !== undefined) return // 호출자 명시값 우선

  const method = `${opts.method ?? 'get'}`.toLowerCase()
  if (method !== 'get') return

  opts.timeout = SSR_GET_TIMEOUT_MS
}

function makebaseURLOptions<
  ResT = void,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  opts?: UseFetchOptions<ResT, DataT, PickKeys, DefaultT, ReqT, Method>,
  keyPrefix?: string
) {
  if (opts) {
    const query = (() => {
      if (opts.query) return opts.query.value ?? opts.query
      if (opts.params) return opts.params.value ?? opts.params
    })()

    const key: QueryKeys | undefined = (() => {
      if (opts.query) return 'query'
      if (opts.params) return 'params'
      return undefined
    })()

    if (query) {
      const newQuery: Record<string, any> = {}

      Object.keys(query).forEach((key) => {
        if (typeof query[key] === 'object') {
          const arrKey = key + '[]'
          newQuery[arrKey] = query[key]
        } else {
          newQuery[key] = query[key]
        }
      })

      // 2026-07-20 엣지 쿼리 정책(관리자 게이트 + _edgeDay) 중앙 적용 — reactive 경로
      applyEdgeQueryPolicy(newQuery)

      if (key === 'query' && opts.query) {
        if (opts.query.value) {
          opts.query.value = newQuery
        } else {
          opts.query = newQuery
        }
      } else if (key === 'params' && opts.params) {
        if (opts.params.value) {
          opts.params.value = newQuery
        } else {
          opts.params = newQuery
        }
      }
    }
  }

  // 서버 사이드에서 클라이언트 IP를 헤더에 추가
  let clientIp: string | undefined
  // 2026-08-12 [IP 스푸핑 차단] X-Nuxt-Client-IP 는 백엔드에서 공격 점수(fence)를 기록할 IP,
  // 차단 판정, 사내 IP 면제에 그대로 쓰인다. 예전에는 헤더만 있으면 백엔드가 무조건 믿었기 때문에
  // 브라우저에서 이 헤더를 직접 붙여 보내는 것만으로 IP 위조가 가능했다.
  // 이제는 아래 시크릿이 일치할 때만 백엔드가 이 헤더를 신뢰한다.
  // (백엔드: doyac/UtilityFunctions.php 의 NUXT_SSR_SECRET / findRealIp())
  let ssrSecret: string | undefined
  if (import.meta.server) {
    try {
      const nuxtApp = useNuxtApp()
      const request = nuxtApp?.ssrContext?.event?.node?.req
      if (request) {
        clientIp = findIpForSSR(request)
      }
      // 서버 전용 runtimeConfig 값이라 클라이언트 번들에는 들어가지 않는다. (modules/nuxt3-module-doyac/index.ts)
      ssrSecret = useRuntimeConfig().dySsrSecret as string | undefined
    } catch (e) {
      // IP 추출 실패 시 무시 (클라이언트 사이드에서 실행되거나 ssrContext가 없는 경우)
    }
  }

  // IP 와 시크릿은 항상 짝으로 보낸다. 하나만 보내면 백엔드가 헤더를 무시한다.
  const ssrIpHeaders = clientIp && ssrSecret
    ? { 'X-Nuxt-Client-IP': clientIp, 'X-DY-SSR-Secret': ssrSecret }
    : undefined

  if (opts) {
    opts.headers = {
      ...opts.headers,
      'Accept': 'application/json',
      ...ssrIpHeaders
    }
    // opts.key = uniqueId()
  } else {
    // opts = { headers: { 'Accept': 'application/json' }, key: uniqueId() }
    opts = {
      headers: {
        'Accept': 'application/json',
        ...ssrIpHeaders
      }
    }
  }

  if (!opts.method || `${opts.method}`.toLowerCase() === 'get') {
    // const params = opts.params ?? opts.query
    // opts.key = (keyPrefix ?? uniqueId()) + '-' + JSON.stringify(params) + '-' + uniqueId()
    // opts.key = (keyPrefix ?? uniqueId()) + '-' + getOptionsKey(opts) + '-' + uniqueId()

    // accessToken을 이용해 키 생성. 토큰이 변경되는 경우에는 새로운 키를 사용해야함. 키가 동일하게 유지되면
    // 다른 아이디로 로그인했을 때 이전 아이디를 사용했을 때와 동일한 키를 사용하게되어 캐시된 데이터가 리턴되기 때문.
    const userStore = useDYUserStore()
    if (userStore.accessToken) {
      // accessToken은 너무 길기 때문에 해시화해서 사용
      // const hash = md5(userStore.accessToken).toString()
      // opts.key = (keyPrefix ?? uniqueId()) + '-' + hash + '-' + getOptionsKey(opts)
      opts.key = (keyPrefix ?? uniqueId()) + '-' + getOptionsKey(opts) + '-' + uniqueId()
    } else {
      opts.key = (keyPrefix ?? uniqueId()) + '-' + getOptionsKey(opts)
    }
  }

  // unjs/h3 라이브러리에서 프록시로 DELETE 요청을 바디 없이 보내면 500 에러가 발생함. Node.js 20 버전 이상에서는
  // 괜찮아진다는데 당장은 업그레이드하기 어려우므로 아래처럼 빈 바디를 강제로 붙여서 해결함. 자세한 내용은 링크 참고.
  // https://github.com/unjs/h3/issues/375
  if (`${opts.method}`.toLowerCase() === 'delete' && !opts.body) {
    opts.body = {}
  }

  // 2026-07-27 SSR GET 60초 상한 (reactive 경로)
  applySsrTimeoutPolicy(opts as Record<string, any>)

  if (import.meta.client && import.meta.dev) {
    return opts
  }

  const { domain, protocol } = useDYAppConfig()

  if (opts) {
    opts.baseURL = getFullDomain({ domain, protocol })
    // opts.key = uniqueId()
  } else {
    // opts = { baseURL: getFullDomain({ domain, protocol }), key: uniqueId() }
    opts = { baseURL: getFullDomain({ domain, protocol }) }
  }

  return opts
}

function makeAuthOptions<
  ResT = void,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  opts?: UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>,
  keyPrefix?: string
) {
  let _opts = makebaseURLOptions(opts, keyPrefix)

  const userStore = useDYUserStore()
  if (!userStore.accessToken) {
    return _opts
  }

  if (_opts) {
    _opts.headers = { ..._opts.headers, 'Authorization': userStore.accessToken }
  } else {
    _opts = { headers: { 'Authorization': userStore.accessToken } }
  }

  return _opts
}

function getRequestKey<ReqT extends NitroFetchRequest>(request: Ref<ReqT> | ReqT | (() => ReqT)) {
  const requestValue = typeof request === 'function' ? request() : request
  const requestKey = typeof requestValue === 'object' && hasOwnProperty(requestValue, 'value') ? requestValue.value : requestValue
  return requestKey
}

function getOptionsKey(opts: any) {
  const extractParams = (opts: any) => {
    if (typeof opts === 'object' && hasOwnProperty(opts, 'params')) {
      return opts.params
    }
    if (typeof opts === 'object' && hasOwnProperty(opts, 'query')) {
      return opts.query
    }
    return undefined
  }
  const params = extractParams(opts)
  // computed 객체인 경우 hasOwnProperty를 사용해서 'value' 속성이 있는지 확인할 수 없음
  let result = ''
  if (params && typeof params === 'object' && params.value) {
    result = JSON.stringify(params.value)
  } else {
    result = JSON.stringify(params)
  }
  if (typeof opts === 'object' && hasOwnProperty(opts, 'body')) {
    result += '-' + JSON.stringify(opts.body)
  }
  return result
}

/**
 * ssr에서 렌더링했던 걸 다시 클라이언트 단계에서 부르지 않음 >> get 요청이면 그냥 씀(캐싱이 되기 때문). extractOrDataFailure를 써야 함.
 * @param request 
 * @param opts 
 * @returns 
 */
export function useDYFetch<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>
) {
  const requestKey = getRequestKey(request)
  // const key = requestKey + '-' + JSON.stringify(opts)
  const key = requestKey + '-' + getOptionsKey(opts)
  const finalOpts = { key, ...makebaseURLOptions(opts, `${requestKey}`) }
  const res = useFetch(request, finalOpts)

  // 2026-07-27 요청 URL 을 응답 객체에도 달아둔다.
  //   에러가 난 경우엔 아래에서 error 에 붙지만, "200 인데 바디가 빈" 경우엔 error 자체가 없어
  //   extractDataOrFailure 가 어떤 엔드포인트였는지 로그에 남길 방법이 없었다. @see _logEmptyBody
  // 2026-08-05 `await` 로 풀린 객체에서 유실되던 문제 수정. @see _stampRequestMeta
  _stampRequestMeta(res, requestKey)

  if (import.meta.server) {
    res.then(r => {
      if (r.error.value) {
        (r.error.value as any).requestUrl = requestKey
      }
    })
  }

  return res
}

export async function useDYFetchData<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>
) {
  const requestKey = getRequestKey(request)
  // const key = requestKey + '-' + JSON.stringify(opts)
  const key = requestKey + '-' + getOptionsKey(opts)
  const response = await useDYFetch(request, { key, immediate: false, ...opts })
  // 2026-07-30 [EMPTY_BODY 로그의 'URL : 알 수 없음' 수정]
  //   useFetch 의 반환값과 그걸 await 로 푼 값은 **서로 다른 객체**다.
  //   (Nuxt 내부: `const p = Promise.resolve(...).then(() => asyncData); Object.assign(p, asyncData); return p`
  //    → 래퍼 p 에 붙인 속성은 await 결과인 asyncData 에는 없다.)
  //   그래서 useDYFetch 가 래퍼에 달아둔 __dyRequestUrl 이 extractDataOrFailure 에선 항상 undefined 였고,
  //   _logEmptyBody 가 어떤 엔드포인트였는지 남기지 못했다. 넘겨줄 객체에 직접 달아준다. @see _logEmptyBody
  ;(response as any).__dyRequestUrl = requestKey
  await response.execute({ dedupe: 'defer' })
  try {
    const data = extractDataOrFailure(response)
    return data
  } catch (e: any) {
    const { isHuman } = useDYUserStore()
    if (typeof e === 'object' && e !== null) {
      e.requestUrl = getRequestKey(request)
    }
    console.error(`useDYFetchData ERROR::isHuman`, isHuman)
    console.error(`useDYFetchData ERROR::서버사이드여부`, import.meta.server)
    console.error(`useDYFetchData ERROR::${request}`, opts)
    throw e
  }
}

export async function useDYApiTemplate<ResponseT, ParamsT = void>(
  request: Parameters<typeof useDYFetch<ResponseT>>[0],
  params: Ref<UnwrapRef<ParamsT>, ParamsT | UnwrapRef<ParamsT>>,
  options?: Parameters<typeof useDYFetch<ResponseT>>[1] & { onError?: (error: NuxtError) => void },
  withToken: boolean = false
) {
  const fetchFunction = withToken ? useDYFetchWithToken : useDYFetch
  const fetcher = await fetchFunction<ResponseT>(
    request,
    {
      ...options,
      params,
      immediate: false,
      watch: false,
      onRequestError: (context) => {
        console.log('onRequestError', context)
        // [2026-04-01] CORS/네트워크 에러 감지 — 모드시큐리티 차단 시에도 CORS 에러로 보일 수 있음
        const requestUrl = context.request || ''
        const message = `네트워크 오류 — 보안 정책에 의해 요청이 차단되었을 수 있습니다.${requestUrl ? ` (${requestUrl})` : ''}`
        options?.onError?.(createError({ statusCode: 0, message }))
      },
      onResponseError: (error) => {
        let message = convertErrorCode(error.response.status)
        if (error.response._data) {
          message = hasOwnProperty(error.response._data, 'message') ? error.response._data.message : convertErrorCode(error.response.status)
        }
        options?.onError?.(createError({ statusCode: error.response.status, message: message }))
      }
    }
  )

  return {
    params, fetcher
  }
}

export async function useDYApiTemplateWithToken<ResponseT, ParamsT = void>(
  request: Parameters<typeof useDYFetchWithToken<ResponseT>>[0],
  params: Ref<UnwrapRef<ParamsT>, ParamsT | UnwrapRef<ParamsT>>,
  options?: Parameters<typeof useDYFetchWithToken<ResponseT>>[1] & { onError?: (error: NuxtError) => void }
) {
  return await useDYApiTemplate<ResponseT, ParamsT>(request, params, options, true)
}

export function useDYLazyFetch<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: Omit<UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>, 'lazy'>
) {
  const requestKey = getRequestKey(request)
  // const key = requestKey + '-' + JSON.stringify(opts)
  const key = requestKey + '-' + getOptionsKey(opts)
  const res = useLazyFetch(request, { key, ...makebaseURLOptions(opts, `${requestKey}`) })
  _stampRequestMeta(res, requestKey) // 2026-08-05 @see useDYFetch 의 동일 주석
  return res
}

export async function useDYLazyFetchData<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: Omit<UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>, 'lazy'>
) {
  const requestKey = getRequestKey(request)
  // const key = requestKey + '-' + JSON.stringify(opts)
  const key = requestKey + '-' + getOptionsKey(opts)
  const response = await useDYLazyFetch(request, { key, ...opts })
  ;(response as any).__dyRequestUrl = requestKey // 2026-07-30 @see useDYFetchData 의 동일 주석(EMPTY_BODY 로그 URL)
  try {
    const data = extractDataOrFailure(response)
    return data
  } catch (e: any) {
    const { isHuman } = useDYUserStore()
    if (typeof e === 'object' && e !== null) {
      e.requestUrl = getRequestKey(request)
    }
    console.error(`useDYLazyFetchData ERROR::isHuman`, isHuman)
    console.error(`useDYLazyFetchData ERROR::서버사이드여부`, import.meta.server)
    console.error(`useDYLazyFetchData ERROR::${request}`, opts)
    throw e
  }
}

/**
 * 토큰이 사용가능한 경우 토큰을 포함하여 `useFetch` 메서드를 실행해주는 메서드. 
 * ssr에서 렌더링했던 걸 다시 클라이언트 단계에서 부르지 않음 >> get 요청이면 그냥 씀(캐싱이 되기 때문). extractOrDataFailure를 써야 함.
 * @param request 
 * @param opts 
 * @returns 
 */
export function useDYFetchWithToken<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>
) {
  const requestKey = getRequestKey(request)
  // let key = requestKey + '-' + JSON.stringify(opts)
  let key = requestKey + '-' + getOptionsKey(opts)
  const userStore = useDYUserStore()
  if (!userStore.accessToken) {
    const res = useFetch(request, { key, ...makebaseURLOptions(opts, `${requestKey}`) })
    _stampRequestMeta(res, requestKey) // 2026-07-27 @see useDYFetch 의 동일 주석
    return res
  }
  key += '-' + userStore.accessToken
  const res = useFetch(request, { key, ...makeAuthOptions(opts, `${requestKey}`) })
  _stampRequestMeta(res, requestKey) // 2026-07-27 @see useDYFetch 의 동일 주석
  return res
}

export async function useDYFetchDataWithToken<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>
) {
  const requestKey = getRequestKey(request)
  // let key = requestKey + '-' + JSON.stringify(opts)
  let key = requestKey + '-' + getOptionsKey(opts)
  const userStore = useDYUserStore()
  if (userStore.accessToken) {
    key += '-' + userStore.accessToken
  }
  const finalOpts = { key, ...opts }
  const response = await useDYFetchWithToken(request, finalOpts)
  ;(response as any).__dyRequestUrl = requestKey // 2026-07-30 @see useDYFetchData 의 동일 주석(EMPTY_BODY 로그 URL)

  if (finalOpts.immediate === true || finalOpts.immediate === undefined) {
    await response.execute({ dedupe: 'defer' })
  }
  try {
    const data = extractDataOrFailure(response)
    return data
  } catch (e: any) {
    const { isHuman } = useDYUserStore()
    if (typeof e === 'object' && e !== null) {
      e.requestUrl = getRequestKey(request)
    }
    console.error(`useDYFetchDataWithToken ERROR::isHuman`, isHuman)
    console.error(`useDYFetchDataWithToken ERROR::서버사이드여부`, import.meta.server)
    console.error(`useDYFetchDataWithToken ERROR::${request}`, opts)
    throw e
  }
}


/**
 * 토큰이 사용가능한 경우 토큰을 포함하여 `useLazyFetch` 메서드를 실행해주는 메서드.
 * @param request 
 * @param opts 
 * @returns 
 */
export function useDYLazyFetchWithToken<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: Omit<UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>, 'lazy'>
): AsyncData<PickFrom<DataT, PickKeys> | DefaultT, ErrorT | null> {
  const requestKey = getRequestKey(request)
  // let key = requestKey + '-' + JSON.stringify(opts)
  let key = requestKey + '-' + getOptionsKey(opts)
  const userStore = useDYUserStore()
  if (!userStore.accessToken) {
    const res = useLazyFetch(request, { key, ...makebaseURLOptions(opts, `${requestKey}`) })
    _stampRequestMeta(res, requestKey) // 2026-08-05 @see useDYFetch 의 동일 주석
    return res
  }
  key += '-' + userStore.accessToken
  const res = useLazyFetch(request, { key, ...makeAuthOptions(opts, `${requestKey}`) })
  _stampRequestMeta(res, requestKey) // 2026-08-05 @see useDYFetch 의 동일 주석
  return res
}

export async function useDYLazyFetchDataWithToken<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  request: Ref<ReqT> | ReqT | (() => ReqT),
  opts?: Omit<UseFetchOptions<_ResT, DataT, PickKeys, DefaultT, ReqT, Method>, 'lazy'>
): Promise<AsyncData<PickFrom<DataT, PickKeys> | DefaultT, ErrorT | null>> {
  const requestKey = getRequestKey(request)
  // let key = requestKey + '-' + JSON.stringify(opts)
  let key = requestKey + '-' + getOptionsKey(opts)
  const userStore = useDYUserStore()
  if (userStore.accessToken) {
    key += '-' + userStore.accessToken
  }
  const response = await useDYLazyFetchWithToken(request, { key, ...opts })
  ;(response as any).__dyRequestUrl = requestKey // 2026-07-30 @see useDYFetchData 의 동일 주석(EMPTY_BODY 로그 URL)
  try {
    const data = extractDataOrFailure(response)
    return data
  } catch (e: any) {
    const { isHuman } = useDYUserStore()
    if (typeof e === 'object' && e !== null) {
      e.requestUrl = getRequestKey(request)
    }
    console.error(`useDYLazyFetchDataWithToken ERROR::isHuman`, isHuman)
    console.error(`useDYLazyFetchDataWithToken ERROR::서버사이드여부`, import.meta.server)
    console.error(`useDYLazyFetchDataWithToken ERROR::${request}`, opts)
    throw e
  }
}

/**
 * `DoyacProtector` 가 요청을 거부할 때 응답 본문에 싣는 문구. 🔴 백엔드와 짝이다 — 한쪽만 바꾸면 {@link isRejectedByProtector} 가 조용히 꺼진다.
 * @see C:\php\home\doyac\laravel\app\Http\Middleware\DoyacProtector.php (handle() 말미 `throw new DoyacProtectorException(...)`)
 */
const PROTECTOR_REJECT_MESSAGE = '비정상적인 요청입니다'

/**
 * 백엔드 방어벽(`DoyacProtector` 미들웨어)이 **요청 자체를 거부**한 에러인지 판정한다. (2026-09-21 신설)
 *
 * ## 왜 필요한가
 * 검색어처럼 «임의 문자열이 들어오는 자리» 에 SQLi 탐침(`?keyword=s'b's'g'l` 류)이 실리면 백엔드가 그 API 요청을
 * 403 으로 거부한다. 그것 자체는 맞는 동작인데, 목록 페이지가 그 403 을 그대로 페이지 응답으로 내보내면
 * ① 그 링크를 누른 사람이 에러 페이지를 보고 ② 색인돼 있던 탐침 URL 을 재크롤한 검색엔진이 «액세스 금지(403)» 로
 * 기록한다(2026-09 GSC 403 급증 조사 — nuxt3-myfirstguide/docs/SEO-INDEXING.md §6-13·§6-34). 검색어가 막힌 건
 * «결과 없음» 이지 페이지 장애가 아니므로, 목록 페이지는 이 판정으로 **빈 결과(200)** 를 그린다.
 * (`/v/search` 는 Promise.allSettled 로 이미 그렇게 동작하고 있었고, 개별 목록 페이지들만 새고 있었다)
 *
 * ## 판정 근거
 * HTTP 403 + 본문 메시지 «비정상적인 요청입니다.» — `DoyacProtector::handle()` 이 던지는 `DoyacProtectorException` 의
 * 문구다(`DoyacExceptionHandler::prepareException` 이 403 `HttpException` 으로 바꾸고 라라벨이 `{message}` JSON 으로 응답).
 * 🔴 같은 403 이라도 `CompanyIpOnly`(«허용되지 않는 접속입니다.») · 차단 IP(`BlockedIpException`=리다이렉트) · 권한 부족은
 *    문구가 달라 **여기에 걸리지 않는다** = 그런 403 은 종전대로 에러 페이지다. 판정을 «403 이면 전부» 로 넓히지 말 것.
 * 🔴 CF 엣지가 낸 403(HTML 본문)도 걸리지 않는다 — 그건 우리 방어벽이 아니라서 «빈 결과» 로 그리면 안 된다.
 *
 * ⚠️ 백엔드 `_checkQueryString` 을 느슨하게 만드는 대안은 택하지 않았다 — 다른 파라미터의 방어가 같이 약해진다.
 *    거부는 그대로 두고, 페이지가 그 거부를 **어떻게 보여줄지만** 바꾼다.
 *
 * 순수 함수라 Nuxt 컨텍스트가 없는 자리(`await` 뒤의 catch 등)에서도 안전하다.
 *
 * @param error `extractDataOrFailure` / `$dy__fetch` 계열이 던진 에러(또는 원시 FetchError). `statusCode` 와
 *              `message`/`data.message` 만 본다.
 */
export function isRejectedByProtector(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const e = error as { statusCode?: unknown, message?: unknown, data?: unknown }
  if (e.statusCode !== 403) return false
  const dataMessage = (typeof e.data === 'object' && e.data !== null) ? (e.data as { message?: unknown }).message : undefined
  return [e.message, dataMessage].some(m => typeof m === 'string' && m.includes(PROTECTOR_REJECT_MESSAGE))
}

/**
 * 응답객체(`AsyncData`)에서 데이터만 추출하는 메서드. 만약 데이터가 없다면 에러를 던진다.
 *
 * @param response
 * @param directHandleError // 기본값은 넉스트의 에러핸들링. 직접 에러핸들링을 해야하는 경우(백엔드에서 보낸 데이터값에 접근등을 위해)
 *                              true로 인자를 넘기고 catch(e) 에서 e.value로 받아 처리. e.value.statusCode 등으로 접근 (직접 넣은 데이터는 e.value.data)
 * @returns 
 */
export function extractDataOrFailure<
  ResT = void,
  ErrorT = FetchError,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = ResT extends void ? 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT> : AvailableRouterMethod<ReqT>,
  _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT,
  DataT = _ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(response: _AsyncData<PickFrom<DataT, PickKeys> | DefaultT, ErrorT | null>, directHandleError = false) {
  if (response.data?.value) {
    return isJsonString(response.data?.value) ? JSON.parse('' + response.data?.value) : response.data.value
  }

  const userStore = useDYUserStore()
  const error = response.error.value

  if (error) {
    // [NEW] 에러 객체에 requestUrl 정보 주입 시도 (없다면)
    if (!(error as any).requestUrl && (error as any).url) {
        (error as any).requestUrl = (error as any).url
    }

    if (directHandleError) throw error

    console.error('error', JSON.stringify(error))

    if (hasOwnProperty(error, 'statusCode') && typeof error.statusCode === 'number') {
      // 2026-07-27 상류에 못 붙은 경우 500 → 502(연결 실패) / 504(타임아웃) 로 바로잡는다. @see _resolveUpstreamStatusCode
      const statusCode = _resolveUpstreamStatusCode(error.statusCode, error)
      //@ts-ignore
      // console.log('_error_error', error.message)
      const extractMessage = (_error: Record<string, unknown>) => {
        // [2026-04-01] CORS/네트워크 차단 에러 감지 (모드시큐리티 등)
        if (hasOwnProperty(_error, 'message') && typeof _error.message === 'string') {
          const networkErrorMsg = _detectNetworkOrCorsError(_error.message)
          if (networkErrorMsg) return networkErrorMsg
        }
        if (hasOwnProperty(_error, 'data') && typeof _error.data === 'object' && _error.data !== null) {
          if (hasOwnProperty(_error.data, 'message')) {
            return _error.data.message ? `${_error.data.message}` : convertErrorCode(statusCode)
          }
        }
        if (hasOwnProperty(_error, 'message')) {
          console.error('_error.message', _error.message)
        }
        if (hasOwnProperty(_error, 'message') && process.dev) {
          return _error.message as string
        }
        return convertErrorCode(statusCode)
      }

      throw createError({
        statusCode: statusCode,
        message: extractMessage(error),
        data: {
          // [NEW] createError 시 data에 requestUrl 정보 포함
          requestUrl: (error as any).requestUrl || (error as any).request || (error as any).url
        }
      })
    }
  }

  // 여기 도달 = 에러도 없는데(=HTTP 통신은 성공) 데이터가 비어 있는 상태.
  //
  // 2026-07-27 ⚠️ 이 400 을 502 로 올리려다 되돌렸다. **이 400 은 "레코드 없음" 신호로 이미 쓰이고 있다.**
  //   `OverChargeDeliveryApiController::getByZip()` 은 해당 우편번호에 추가배송비가 없으면 `null` 을 반환한다
  //   (= HTTP 200 + 빈 바디, 대부분의 우편번호가 이 경우). 그걸 받은 dy-over-charge-delivery.ts 의
  //   `getByZip()` 이 `statusCode === 400` 만 조용히 삼키고 나머지는 throw 하는 구조라,
  //   여기서 502 를 던지면 **결제 1단계 주소 입력에서 에러 페이지가 뜬다.**
  //   같은 패턴(200 + null = 없음)을 쓰는 다른 엔드포인트가 더 있을 수 있어 전역 변경은 위험하다.
  //
  //   대신 SSR 에서 발생한 건만 에러로그에 남겨(_logEmptyBody) 어떤 엔드포인트가 실제로 빈 응답을 주는지
  //   먼저 관측한다. 로그로 실태가 파악되면 그때 해당 엔드포인트만 404/502 로 바로잡을 것.
  //   (근본 해결은 API 가 "없음" 을 200+null 이 아니라 404 로 응답하게 만드는 것)

  // 2026-10-08 [센트리 NUXT3-DOYACWEB-11G / NUXT3-DOYACART-QF·QG] **화면을 떠나서 Nuxt 가 버린 요청**은 «없음» 이 아니다.
  //   요청을 건 컴포넌트가 사라지면 Nuxt(purgeCachedData)가 200 응답도 버리고 status 를 idle 로 되돌린다.
  //   그동안 이걸 아래 «빈 응답(400)» 으로 오인해 다음 화면에 가짜 오류 알림·센트리 보고가 생겼다. @see libs/discarded-request.ts
  //   판정은 두 조건 AND — ①status 가 idle(완료된 요청은 빈 바디여도 success 다) ②주인 scope 가 실제로 사라졌다
  //   (@see _stampRequestMeta 의 ownerDisposed). ②가 없으면 «execute 를 깜빡 안 부른» 진짜 버그까지 숨기게 된다.
  //   🔴 statusCode 는 400 그대로 둔다 — 400 을 «없음» 으로 조용히 삼키는 호출부(getByZip 등)의 흐름을 바꾸지 않기 위해서.
  //      표식만 붙이고, 알림·에러화면·에러로그·센트리가 표식을 보고 건너뛴다.
  //   console.warn 은 센트리 행동 이력(breadcrumb)에 남기 위한 것이다 — 이 건 자체는 보고하지 않는다.
  if (import.meta.client && response.status?.value === 'idle' && (response as any).__dyRequestMeta?.ownerDisposed === true) {
    console.warn('[fetch] 화면을 떠나 버려진 요청(오류 아님)', (response as any).__dyRequestUrl ?? '')
    throw createError({ statusCode: 400, message: '요청한 결과를 찾을 수 없습니다.', data: { [DY_DISCARDED_REQUEST]: true } })
  }

  console.error('response.status', response.status.value)
  _logEmptyBody(response)
  throw createError({ statusCode: 400, message: '요청한 결과를 찾을 수 없습니다.' })
}

/**
 * 2026-07-27 "HTTP 200 인데 바디가 빈" 응답을 서버 에러로그(E:\PHP_ERROR)에 남긴다.
 *
 * 이 상황은 브라우저 콘솔에만 찍히면 아무도 모르고 지나간다(특히 봇 크롤 중 SSR 에서 발생한 건 확인할 방법이 없다).
 * 어떤 엔드포인트가 빈 응답을 주는지 추적할 수 있어야 원인(캐시 오염/상류 이상/설계상 "없음" 표현)을 구분할 수 있다.
 *
 * **SSR 에서만** 남긴다. 클라이언트에서는 `getByZip` 처럼 "없음" 을 200+null 로 표현하는 정상 호출이
 * 빈번해 로그가 범람한다(결제 주소 입력마다 발생). SSR 페이지에서 발생한 건은 페이지가 깨진다는 뜻이라 관측 가치가 있다.
 *
 * 로깅 실패가 페이지 렌더를 막으면 안 되므로 전 구간 try/catch 로 감싼다.
 */
function _logEmptyBody(response: any): void {
  if (!import.meta.server) return

  try {
    const req = response?.__dyRequestMeta?.req
    const headers: Record<string, any> = req?.headers ?? {}

    logToFile(
      _resolveAppLabel(),
      '[EMPTY_BODY] API 가 200 을 반환했으나 바디가 비어 있음 (SSR) → 400 으로 전파',
      ` | API : ${response?.__dyRequestUrl ?? '알 수 없음(useDYFetch 계열이 아닌 AsyncData)'}`,
      ` | 페이지 : ${_describeSsrPage(req)}`,
      ` | UA : ${headers['user-agent'] ?? '-'}`,
      ` | Referer : ${headers['referer'] ?? '-'}`,
      ` | IP : ${_safeCall(() => (req ? findIpForSSR(req) : undefined)) ?? '-'}`,
      ` | data.value : ${_describeValue(response?.data?.value)}`,
      ` | error.value : ${_describeValue(response?.error?.value)}`,
      ` | asyncData.status : ${String(response?.status?.value)}`,
      ` | 호출부 : ${_shortStack()}`
    )
  } catch {
    // 로깅 실패는 무시 (렌더를 막지 않는다)
  }
}

/**
 * 2026-08-05 로그에 쓸 앱 식별자.
 *
 * `useDYAppConfig()` 는 Nuxt 인스턴스가 살아 있어야 하는데, EMPTY_BODY 가 찍히는 시점엔
 * 이미 인스턴스가 소실돼 있는 경우가 많아 실제 로그가 전부 `알 수 없음` 으로만 남았다
 * (= 어느 사이트에서 난 건지조차 알 수 없었다).
 * → 인스턴스와 무관한 PM2 앱명(`process.env.name`) / 프로세스 작업디렉터리를 대체값으로 쓴다.
 */
function _resolveAppLabel(): string {
  const appName = _safeCall(() => useDYAppConfig().appName)
  if (appName) return `${appName}`

  return _safeCall(() => {
    const pm2Name = process.env?.name || process.env?.pm_id
    if (pm2Name) return `pm2:${pm2Name}`
    return typeof process.cwd === 'function' ? `cwd:${process.cwd()}` : undefined
  }) ?? '알 수 없음'
}

/**
 * 2026-08-05 SSR 이 렌더 중이던 **페이지** URL. (어떤 API 가 빈 바디를 줬는지만으로는 재현이 안 되므로 필수)
 * 리버스 프록시 뒤라 `x-forwarded-*` 를 우선한다. @see getCurrentRequestHost (config.ts)
 */
function _describeSsrPage(req: any): string {
  if (!req) return '알 수 없음(요청 컨텍스트 유실)'

  return _safeCall(() => {
    const headers: Record<string, any> = req.headers ?? {}
    const first = (value: any) => `${value ?? ''}`.split(',')[0].trim()
    const proto = first(headers['x-forwarded-proto']) || 'https'
    const host = first(headers['x-forwarded-host']) || first(headers['host'])
    return `${proto}://${host}${req.url ?? ''}`
  }) ?? '알 수 없음'
}

/**
 * 2026-08-05 값 요약. **"빈 바디가 정상인지" 를 판별하려면 실제 값이 필요하다.**
 * `null` 이면 API 가 의도적으로 "없음" 을 표현한 것(→ 그 엔드포인트를 404 로 고칠 대상),
 * `undefined` / `''` 면 응답 자체가 유실된 것(→ 상류·캐시 이상)으로 갈린다.
 */
function _describeValue(value: any): string {
  if (value === undefined) return 'undefined'
  if (value === null) return 'null'

  return _safeCall(() => {
    const json = typeof value === 'string' ? `"${value}"` : JSON.stringify(value)
    return `${typeof value} ${`${json}`.slice(0, 200)}`
  }) ?? typeof value
}

/**
 * 2026-08-05 호출부 추적. API URL 조차 못 남기는 경우(useDYFetch 계열이 아닌 AsyncData 를
 * extractDataOrFailure 에 그대로 넘긴 호출부)에 어느 코드에서 왔는지 알아내는 최후 수단이다.
 * (0:Error 1:_shortStack 2:_logEmptyBody 3:extractDataOrFailure 부터가 실제 호출부)
 */
function _shortStack(): string {
  return _safeCall(() => {
    const lines = `${new Error().stack ?? ''}`.split('\n').slice(3, 8).map(line => line.trim()).filter(Boolean)
    return lines.length ? lines.join(' < ') : undefined
  }) ?? '-'
}

/** 로깅용 안전 실행 — 진단 정보를 모으다가 렌더를 막는 일이 없게 한다. */
function _safeCall<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

/**
 * 2026-08-05 요청 메타(엔드포인트 URL + SSR 요청 컨텍스트)를 응답 객체에 심는다. @see _logEmptyBody
 *
 * ⚠️ **`await` 로 풀면 다른 객체가 나온다.** Nuxt 의 `useAsyncData` 는
 * `Object.assign(asyncDataPromise, asyncReturn); return asyncDataPromise` 구조라
 * (nuxt 3.17.5 `dist/app/composables/asyncData.js:149-160`),
 * `await` 는 `.then(() => asyncReturn)` 을 타고 **속성을 복사받지 못한 내부 객체(asyncReturn)** 를 돌려준다.
 * 즉 프로미스 객체에만 심으면 `const response = await useDYFetch(...)` 를 쓰는 모든 경로
 * (`useDYFetchData` / `useDYLazyFetchData` / `useDYFetchDataWithToken` / `useDYLazyFetchDataWithToken` 및
 * 페이지에서 직접 `await` 하는 호출부)에서 **전부 유실된다.**
 * → 2026-07-27~08-05 사이 EMPTY_BODY 로그의 URL 이 예외 없이 `알 수 없음` 으로 찍힌 원인이었다.
 *
 * 그래서 프로미스와 resolve 결과 **양쪽에** 심는다.
 * 여기서 등록한 `.then` 은 호출부의 `await` 보다 먼저 등록되므로 실행 순서(FIFO)가 보장된다.
 *
 * SSR 요청 객체(`req`)를 같이 들고 있는 이유 — 로그를 남기는 시점엔 Nuxt 인스턴스가 이미 소실돼
 * `useNuxtApp()` / `useRequestHeaders()` 로는 페이지 URL·UA 를 알아낼 수 없다. **살아 있을 때 참조를 떠둔다.**
 *
 * 2026-10-08 `ownerDisposed` 추가 — 이 요청을 건 컴포넌트(effect scope)가 사라졌는가. @see libs/discarded-request.ts
 *   Nuxt 가 같은 시점(scope dispose)에 결과를 버리므로, `extractDataOrFailure` 가 «버려진 요청» 과
 *   «서버가 준 빈 응답» 을 가르는 근거가 된다. meta 객체 하나를 프로미스·resolve 결과가 **공유**하므로
 *   어느 쪽을 들고 있든 같은 값을 본다.
 *   ⚠️ 반드시 `useFetch` 를 부른 **같은 동기 구간**에서 호출돼야 한다(그래야 같은 scope 다) — 모든 래퍼가 그렇게 부른다.
 *   scope 가 없는 곳(이벤트 핸들러·await 뒤)에서 만든 요청은 Nuxt 도 버리지 않으므로 표시할 필요가 없다.
 */
function _stampRequestMeta(res: any, requestKey: string): void {
  const meta = { url: requestKey, req: _safeCall(() => (import.meta.server ? useNuxtApp()?.ssrContext?.event?.node?.req : undefined)), ownerDisposed: false }

  if (import.meta.client && getCurrentScope()) {
    onScopeDispose(() => { meta.ownerDisposed = true })
  }

  const stamp = (target: any) => {
    if (!target) return
    try {
      target.__dyRequestUrl = requestKey
      target.__dyRequestMeta = meta
    } catch {
      // freeze 된 객체 등 — 진단용이므로 실패해도 무시
    }
  }

  stamp(res)
  _safeCall(() => res?.then?.((resolved: any) => stamp(resolved))?.catch?.(() => { }))
}

/**
 * 2026-07-27 상류(API) 통신 자체가 실패한 경우의 상태코드를 바로잡는다.
 *
 * 문제 — ofetch 는 응답이 없을 때 FetchError.statusCode 를 undefined 로 두고(`ctx.response?.status` getter),
 * Nuxt useAsyncData 가 감쌀 때 h3 `createError` 가 `sanitizeStatusCode(undefined, 500)` 로 **500** 을 채운다.
 * 그 결과 "API 서버에 못 붙었다" 와 "우리 코드가 터졌다" 가 둘 다 500 으로 나가 구분이 안 됐다.
 *
 * SSR 은 API 앞단의 게이트웨이 역할이므로 상류에 못 붙은 건 **502**, 상류가 응답을 안 준 건 **504** 가 맞다.
 * 구글봇은 어느 5xx 든 크롤 속도를 낮추고 재시도하지만, 우리 에러로그·모니터링에서
 * "API 장애" 와 "Nuxt 버그" 를 구분할 수 있다는 실익이 크다.
 *
 * 판별 근거 — ofetch `createFetchError` 는 응답이 없을 때만 메시지에 `<no response>` 를 넣는다.
 * 상류가 실제로 500 을 준 경우엔 `500 Internal Server Error` 가 들어가므로 확실히 구분된다.
 * (실측: 네트워크오류/타임아웃만 `<no response>` 포함, 상류 500/502 는 미포함)
 *
 * 502 / 504 구분은 **메시지로는 안 된다.** undici 의 headersTimeout(연결은 됐는데 응답 헤더가 안 옴)은
 * ofetch 메시지가 연결 거부와 똑같이 `<no response> fetch failed` 로 나오고,
 * 진짜 원인은 cause 2단계 아래 `HeadersTimeoutError(UND_ERR_HEADERS_TIMEOUT)` 에 들어있다.
 * 그래서 cause 체인을 따라가 타임아웃 여부를 본다. (실측한 체인 구조:
 *  FetchError → TypeError('fetch failed') → HeadersTimeoutError)
 */
function _resolveUpstreamStatusCode(statusCode: number, error: any): number {
  if (statusCode !== 500) return statusCode // 상류가 실제로 준 코드는 그대로 통과

  const message = error?.message
  if (typeof message !== 'string' || !message.includes('<no response>')) return statusCode

  return _isUpstreamTimeout(error) ? 504 : 502
}

/**
 * 2026-07-27 상류 무응답(타임아웃)인지 판정. cause 체인을 훑어 undici 타임아웃 코드를 찾는다.
 *
 * - `UND_ERR_HEADERS_TIMEOUT` : 연결은 됐는데 응답 헤더가 안 옴 (기본 300초)
 *   → API 서버가 커넥션은 받았지만 응답을 못 만드는 상태(워커 고갈 / DB 락 대기 / 외부 API 대기 등)
 * - `UND_ERR_BODY_TIMEOUT`    : 헤더는 왔는데 본문이 끊김 (기본 300초)
 * - `UND_ERR_CONNECT_TIMEOUT` : TCP 연결 자체가 안 붙음 (기본 10초)
 * - `TimeoutError`            : ofetch `timeout` 옵션(AbortController)에 의한 중단
 */
function _isUpstreamTimeout(error: any): boolean {
  const TIMEOUT_CODES = ['UND_ERR_HEADERS_TIMEOUT', 'UND_ERR_BODY_TIMEOUT', 'UND_ERR_CONNECT_TIMEOUT']

  let cursor = error
  for (let depth = 0; cursor && depth < 5; depth++) { // 순환 참조 방어 겸 깊이 제한
    if (typeof cursor.code === 'string' && TIMEOUT_CODES.includes(cursor.code)) return true
    if (cursor.name === 'TimeoutError' || cursor.name === 'AbortError') return true
    cursor = cursor.cause
  }
  return false
}

/**
 * [2026-04-01] CORS/네트워크 차단 에러 메시지 감지 및 사용자 친화적 메시지 변환
 * 모드시큐리티 등 보안정책에 의한 차단 시 브라우저에서 CORS 에러로 표시되는 경우 처리
 * - "<no response> Failed to fetch" 패턴: 응답 자체가 없음 → 보안정책 차단 가능성
 * - "Failed to fetch" 만 포함: 네트워크 에러 또는 CORS 차단
 */
function _detectNetworkOrCorsError(message: string): string | null {
  if (typeof message !== 'string') return null
  // ofetch가 생성하는 "<no response> Failed to fetch" 패턴 감지
  if (message.includes('<no response>') || message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('CORS')) {
    // URL 추출 시도 (ofetch 메시지 형식: [METHOD] "URL": <no response> Failed to fetch)
    const urlMatch = message.match(/"([^"]+)"/)
    const url = urlMatch ? urlMatch[1] : ''
    // 짧은 URL 표시 (너무 긴 경우 쿼리스트링 제거)
    const shortUrl = url.length > 80 ? url.split('?')[0] + '?...' : url
    return `네트워크 오류 — 보안 정책에 의해 요청이 차단되었을 수 있습니다.${shortUrl ? `\n(${shortUrl})` : ''}`
  }
  return null
}

function handleFetchError<ResT = void>(error: any) {
  // SSR 환경에서 Nuxt 인스턴스 소실 에러가 발생한 경우 파일로 로깅
  if (import.meta.server && error?.message?.includes('[nuxt] instance unavailable')) {
    console.error('handleFetchError::error', error)
    logToFile('알 수 없음(Nuxt 인스턴스 소실로 appConfig 접근 불가)', `[INSTANCE_UNAVAILABLE] ${error.message}`, ` - Stack : ${error.stack || 'N/A'}`)
  }

  if (hasOwnProperty(error, 'statusCode') && typeof error.statusCode === 'number') {
    // 2026-07-27 상류에 못 붙은 경우 500 → 502/504 로 바로잡는다. @see _resolveUpstreamStatusCode
    const statusCode = _resolveUpstreamStatusCode(error.statusCode, error)
    //@ts-ignore
    // console.log('_error_error', error.message)
    const extractMessage = (_error: Record<string, unknown>) => {
      // [2026-04-01] CORS/네트워크 차단 에러 감지 (모드시큐리티 등)
      if (hasOwnProperty(_error, 'message') && typeof _error.message === 'string') {
        const networkErrorMsg = _detectNetworkOrCorsError(_error.message)
        if (networkErrorMsg) return networkErrorMsg
      }
      if (hasOwnProperty(_error, 'data') && typeof _error.data === 'object' && _error.data !== null) {
        if (hasOwnProperty(_error.data, 'message')) {
          return _error.data.message ? `${_error.data.message}` : convertErrorCode(statusCode)
        }
      }
      if (hasOwnProperty(_error, 'message')) {
        console.error('_error.message', _error.message)
      }
      if (hasOwnProperty(_error, 'message') && process.dev) {
        return _error.message as string
      }
      return convertErrorCode(statusCode)
    }

    return createError({
      statusCode: statusCode,
      message: extractMessage(error)
    })
  } else {
    // 2026-07-27 이 경로($dy__fetch 계열)는 **원시 FetchError** 를 그대로 받는다.
    //   ofetch 는 응답이 없으면 statusCode 를 undefined 로 두므로(getter = ctx.response?.status)
    //   위 분기를 타지 못하고 여기로 떨어져 **네트워크 오류가 400 으로** 나가고 있었다.
    //   4xx 는 구글에게 "이 페이지는 잘못됐다" 로 읽혀 재크롤 대신 색인에서 내려갈 수 있다.
    //   → 상류 통신 실패로 식별되면 502/504 로 내보낸다. (useDYFetch 경로는 Nuxt 가 500 으로 채워줘 위 분기를 탄다)
    const upstreamStatusCode = _resolveUpstreamStatusCode(500, error)
    if (upstreamStatusCode !== 500) {
      return createError({
        error: error,
        statusCode: upstreamStatusCode,
        message: _detectNetworkOrCorsError(error?.message) ?? convertErrorCode(upstreamStatusCode)
      })
    }

    return createError({ error: error, statusCode: 400, message: '요청한 결과를 찾을 수 없습니다.' })
  }
}

/**
 * post, patch 등등의 요청인 경우 캐싱을 안하는게 좋기 때문에 이걸 쓸 것, extractOrDataFailure를 쓰지 말것!
 * @param request 
 * @param options 
 * @returns 
 */
export async function $dy__fetchWithToken<
  ResT = void,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = AvailableRouterMethod<ReqT>,
>(
  request: ReqT,
  options?: NitroFetchOptions<ReqT, Method>
): Promise<ResT> {
  try {
    const mergedOptions = _dyBuildFetchOptions<ReqT, Method>(options, true)
    return await $fetch<ResT>(request as any, mergedOptions as any)
  } catch (e: any) {
    throw handleFetchError(e)
  }
}

/**
 * post, patch 등등의 요청인 경우 캐싱을 안하는게 좋기 때문에 이걸 쓸 것, extractOrDataFailure를 쓰지 말것!
 * @param request 
 * @param options 
 * @returns 
 */
export async function $dy__fetch<
  ResT = void,
  ReqT extends NitroFetchRequest = NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT> = AvailableRouterMethod<ReqT>,
>(
  request: ReqT,
  options?: NitroFetchOptions<ReqT, Method>
): Promise<ResT> {
  try {
    const mergedOptions = _dyBuildFetchOptions<ReqT, Method>(options, false)
    return await $fetch<ResT>(request as any, mergedOptions as any)
  } catch (e: any) {
    throw handleFetchError(e)
  }
}

function _dyBuildFetchOptions<
  ReqT extends NitroFetchRequest,
  Method extends AvailableRouterMethod<ReqT>
>(
  options: NitroFetchOptions<ReqT, Method> | undefined,
  includeToken: boolean
): NitroFetchOptions<ReqT, Method> {
  const mergedOptions = { ...(options as any ?? {}) } as Record<string, any>

  // 서버 사이드에서 클라이언트 IP를 헤더에 추가
  let clientIp: string | undefined
  if (import.meta.server) {
    try {
      const nuxtApp = useNuxtApp()
      const request = nuxtApp?.ssrContext?.event?.node?.req
      if (request) {
        clientIp = findIpForSSR(request)
      }
    } catch (e) {
      console.error('findIpForSSR ERROR', e)
      // IP 추출 실패 시 무시 (클라이언트 사이드에서 실행되거나 ssrContext가 없는 경우)
    }
  }

  // Accept 헤더 기본값
  mergedOptions.headers = {
    ...(options?.headers as Record<string, any> | undefined),
    Accept: 'application/json',
    ...(clientIp && { 'X-Client-IP': clientIp })
  }

  // Authorization 헤더 (옵션)
  if (includeToken) {
    const userStore = useDYUserStore()
    if (userStore.accessToken) {
      mergedOptions.headers.Authorization = userStore.accessToken
    }
  }

  // params/query 배열 키 브래킷 변환
  const querySource = (() => {
    if (mergedOptions.query) return mergedOptions.query?.value ?? mergedOptions.query
    if (mergedOptions.params) return mergedOptions.params?.value ?? mergedOptions.params
  })()

  const queryKey: 'query' | 'params' | undefined = (() => {
    if (mergedOptions.query) return 'query'
    if (mergedOptions.params) return 'params'
    return undefined
  })()

  if (querySource) {
    const newQuery: Record<string, any> = {}
    Object.keys(querySource).forEach((k) => {
      if (typeof querySource[k] === 'object') {
        const arrKey = k + '[]'
        newQuery[arrKey] = querySource[k]
      } else {
        newQuery[k] = querySource[k]
      }
    })
    // 2026-07-20 엣지 쿼리 정책(관리자 게이트 + _edgeDay) 중앙 적용 — imperative 경로(makebaseURLOptions 와 동일 방침)
    applyEdgeQueryPolicy(newQuery)
    if (queryKey === 'query' && mergedOptions.query) {
      if (mergedOptions.query.value) mergedOptions.query.value = newQuery
      else mergedOptions.query = newQuery
    } else if (queryKey === 'params' && mergedOptions.params) {
      if (mergedOptions.params.value) mergedOptions.params.value = newQuery
      else mergedOptions.params = newQuery
    }
  }

  // DELETE 바디 보정
  if (`${mergedOptions.method ?? ''}`.toLowerCase() === 'delete' && !mergedOptions.body) {
    mergedOptions.body = {}
  }

  // 2026-07-27 SSR GET 60초 상한 (imperative 경로 — makebaseURLOptions 와 동일 방침)
  applySsrTimeoutPolicy(mergedOptions)

  // baseURL 설정 (개발 클라이언트에서는 생략)
  if (!(import.meta.client && import.meta.dev)) {
    const { domain, protocol } = useDYAppConfig()
    mergedOptions.baseURL = getFullDomain({ domain, protocol })
  }

  return mergedOptions as unknown as NitroFetchOptions<ReqT, Method>
}
